<template>
  <v-container fluid class="pa-4">
    <!-- Notificaciones Flotantes Toast (Esquina Superior Derecha) -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      top
      right
      :timeout="snackbar.timeout"
      elevation="6"
    >
      <div class="d-flex align-center">
        <v-icon left dark class="mr-2">{{ snackbar.icon }}</v-icon>
        <span class="font-weight-bold body-2 white--text">{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text dark v-bind="attrs" @click="snackbar.show = false" class="font-weight-bold">
          CERRAR
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Encabezado del Módulo -->
    <v-row class="mb-2">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <div>
          <h2 class="text-h5 font-weight-bold primary--text">
            <v-icon color="primary" class="mr-2">mdi-bank-transfer</v-icon>
            Conciliación Bancaria Automática y Manual
          </h2>
          <span class="grey--text text--darken-1 subtitle-2">
            Gestión y cruce de extractos bancarios contra recibos de pago
          </span>
        </div>
        <v-btn color="primary" outlined @click="refrescarTodo">
          <v-icon left>mdi-refresh</v-icon> Actualizar
        </v-btn>
      </v-col>
    </v-row>

    <!-- Pestañas principales -->
    <v-card elevation="2" class="rounded-lg">
      <v-tabs v-model="tab" background-color="primary" dark class="rounded-t-lg">
        <v-tab key="tab-matching">
          <v-icon left>mdi-file-compare</v-icon> Tablero de Coincidencias
        </v-tab>
        <v-tab key="tab-cargas">
          <v-icon left>mdi-cloud-upload</v-icon> Cargar Extracto Bancario
        </v-tab>
        <v-tab key="tab-extractos">
          <v-icon left>mdi-table-clock</v-icon> Historial de Extractos
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- ---------------------------------------------------- -->
        <!-- TAB 1: TABLERO DE COINCIDENCIAS (MATCHING) -->
        <!-- ---------------------------------------------------- -->
        <v-tab-item key="tab-matching" class="pa-3 dashboard-viewport">
          <!-- Filtros de búsqueda -->
          <v-card outlined class="mb-3 pa-3 grey lighten-4">
            <v-row dense align="center">
              <v-col cols="12" sm="2">
                <v-select
                  v-model="filtros.banco"
                  :items="opcionesBancos"
                  label="Banco"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="cargarExtractosTablero"
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="filtros.nro_cuenta"
                  label="N° de Cuenta Bancaria"
                  placeholder="Ej. 8128740024"
                  outlined
                  dense
                  hide-details
                  @change="cargarExtractosTablero"
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="filtros.estado"
                  :items="opcionesEstados"
                  label="Estado Conciliación"
                  outlined
                  dense
                  hide-details
                  @change="cargarExtractosTablero"
                />
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="filtros.fechaDesde"
                  label="Desde"
                  type="date"
                  outlined
                  dense
                  hide-details
                  @change="cargarExtractosTablero"
                />
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="filtros.fechaHasta"
                  label="Hasta"
                  type="date"
                  outlined
                  dense
                  hide-details
                  @change="cargarExtractosTablero"
                />
              </v-col>
            </v-row>
          </v-card>

          <!-- Barra Ejecutiva de Control de Balance de Conciliación -->
          <v-card outlined class="pa-3 mb-2 rounded-lg elevation-1 white" style="border-left: 5px solid #1565C0;">
            <div class="d-flex justify-space-between align-center mb-2 flex-wrap">
              <div class="subtitle-2 font-weight-bold grey--text text--darken-3 d-flex align-center">
                <v-icon color="primary" class="mr-1" small>mdi-scale-balance</v-icon>
                AUDITORÍA DE BALANCE FINANCIERO
                <span class="caption grey--text text--darken-1 ml-2">({{ resumenBalance.periodo_conciliacion || 'Período Actual' }} | Banco: {{ resumenBalance.banco || 'TODOS' }})</span>
              </div>
              <v-chip
                small
                :color="esCuadrado ? 'success' : 'error'"
                dark
                class="font-weight-bold px-3"
              >
                <v-icon x-small class="mr-1">{{ esCuadrado ? 'mdi-check-decagram' : 'mdi-alert-octagon' }}</v-icon>
                {{ esCuadrado ? 'BALANCEADO 100% (AUDITORÍA EXITOSA)' : `DISCREPANCIA DETECTADA: Bs ${formatMonto(Math.abs(resumenBalance.auditoria_balance ? resumenBalance.auditoria_balance.diferencia : 0))}` }}
              </v-chip>
            </div>

            <!-- Grid de 4 Métricas KPI -->
            <v-row dense>
              <v-col cols="12" sm="3">
                <v-card flat class="pa-2 grey lighten-4 rounded border-light">
                  <div class="caption font-weight-bold grey--text text--darken-2">🏦 1. Total Ingresado Banco (T<sub>banco</sub>)</div>
                  <div class="title font-weight-bold blue--text text--darken-3">Bs {{ formatMonto(resumenBalance.metricas_cabecera ? resumenBalance.metricas_cabecera.total_ingreso_banco : 0) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card flat class="pa-2 green lighten-5 rounded border-light">
                  <div class="caption font-weight-bold green--text text--darken-4">🧾 2. Recibos Conciliados (T<sub>recibos_mes</sub>)</div>
                  <div class="title font-weight-bold green--text text--darken-3">Bs {{ formatMonto(resumenBalance.metricas_cabecera ? resumenBalance.metricas_cabecera.total_importe_recibos_mes : 0) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card flat class="pa-2 orange lighten-5 rounded border-light">
                  <div class="caption font-weight-bold orange--text text--darken-4">🔒 3. Fondos en Custodia (F<sub>custodia</sub>)</div>
                  <div class="title font-weight-bold orange--text text--darken-3">Bs {{ formatMonto(resumenBalance.metricas_cabecera ? resumenBalance.metricas_cabecera.saldo_fondos_custodia : 0) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card flat class="pa-2 purple lighten-5 rounded border-light">
                  <div class="caption font-weight-bold purple--text text--darken-4">📅 4. Recibos Meses Anteriores (R<sub>anteriores</sub>)</div>
                  <div class="title font-weight-bold purple--text text--darken-3">Bs {{ formatMonto(resumenBalance.metricas_cabecera ? resumenBalance.metricas_cabecera.recibos_meses_anteriores_registrados : 0) }}</div>
                </v-card>
              </v-col>
            </v-row>
          </v-card>

          <!-- Split View: Extractos (Izquierda) vs Recibos/Sugerencias (Derecha) -->
          <v-row dense align="start">
            <!-- LADO IZQUIERDO: MOVIMIENTOS BANCARIOS -->
            <v-col cols="12" md="6">
              <v-card outlined class="dashboard-panel-card">
                <v-card-title class="subtitle-1 font-weight-bold blue lighten-5 py-2 d-flex justify-space-between align-center">
                  <span>
                    <v-icon color="info" class="mr-1">mdi-format-list-bulleted</v-icon>
                    Movimientos del Banco
                  </span>
                  <v-chip x-small color="primary" dark class="font-weight-bold">
                    {{ extractos.length }} extractos
                  </v-chip>
                </v-card-title>
                <v-data-table
                  :headers="headersExtractos"
                  :items="extractos"
                  :loading="cargandoExtractos"
                  item-key="id"
                  dense
                  fixed-header
                  hide-default-footer
                  :items-per-page="-1"
                  height="calc(100vh - 250px)"
                  class="elevation-0"
                  @click:row="seleccionarExtracto"
                  :item-class="getFilaClass"
                >
                  <template v-slot:[`item.banco`]="{ item }">
                    <div>
                      <div class="font-weight-bold primary--text" style="font-size: 11px;">{{ item.banco }}</div>
                      <div class="grey--text text--darken-2" style="font-size: 10px; line-height: 1;" v-if="item.nro_cuenta">
                        ({{ item.nro_cuenta }})
                      </div>
                    </div>
                  </template>
                  <template v-slot:[`item.credito`]="{ item }">
                    <span class="green--text text--darken-3 font-weight-bold">
                      Bs {{ formatMonto(item.credito) }}
                    </span>
                  </template>
                  <template v-slot:[`item.estado`]="{ item }">
                    <v-chip
                      x-small
                      :color="getColorEstado(item.estado)"
                      text-color="white"
                      class="font-weight-medium"
                    >
                      {{ item.estado || 'SIN_CONCILIAR' }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>

            <!-- LADO DERECHO: DETALLE DEL MATCH SELECCIONADO / SUGERENCIAS -->
            <v-col cols="12" md="6">
              <v-card outlined v-if="extractoSeleccionado" class="dashboard-panel-card">
                <v-card-title class="subtitle-1 font-weight-bold green lighten-5 py-2 d-flex justify-space-between">
                  <span>
                    <v-icon color="success" class="mr-1">mdi-link-variant</v-icon>
                    Detalle Extracto #{{ extractoSeleccionado.id }} ({{ extractoSeleccionado.banco }})
                  </span>
                  <v-chip color="primary" small dark>
                    Monto: Bs {{ formatMonto(extractoSeleccionado.credito) }}
                  </v-chip>
                </v-card-title>

                <v-card-text class="pt-3 scrollable-detail-body">
                  <div class="mb-3 caption">
                    <strong>Fecha:</strong> {{ extractoSeleccionado.fecha_movimiento }} |
                    <strong>Glosa:</strong> {{ extractoSeleccionado.glosa || extractoSeleccionado.descripcion || 'Sin detalle' }} |
                    <strong>Ref:</strong> {{ extractoSeleccionado.referencia || 'N/A' }}
                  </div>

                  <!-- Sugerencias Automáticas o Conciliados si existen -->
                  <div v-if="sugerencias && sugerencias.length > 0">
                    <!-- Banner de Sugerencia Multi-Recibo (1 Depósito = N Recibos) -->
                    <v-alert
                      v-if="sugerencias.length >= 2 && sugerencias[0].estado === 'SUGERIDO'"
                      dense
                      colored-border
                      type="info"
                      elevation="2"
                      class="mb-3 purple lighten-5 border-purple pa-3"
                    >
                      <div class="d-flex justify-space-between align-center">
                        <div>
                          <div class="subtitle-2 font-weight-bold purple--text text--darken-3">
                            <v-icon color="purple darken-3" small class="mr-1">mdi-account-group</v-icon>
                            Sugerencia Multi-Recibo ({{ sugerencias.length }} Recibos Agrupados)
                          </div>
                          <div class="caption grey--text text--darken-3">
                            Suma Total: <strong>Bs {{ formatMonto(calcularTotalSugerencias(sugerencias)) }}</strong>
                          </div>
                        </div>
                        <v-btn
                          color="purple darken-2"
                          dark
                          small
                          class="font-weight-bold"
                          :loading="guardandoMatch"
                          @click="aprobarGrupoRecibos(sugerencias)"
                        >
                          <v-icon left small>mdi-check-all</v-icon>
                          Aprobar Grupo ({{ sugerencias.length }})
                        </v-btn>
                      </div>
                    </v-alert>

                    <div class="subtitle-2 font-weight-bold mb-2 orange--text text--darken-3 d-flex align-center">
                      <v-icon color="orange darken-3" small class="mr-1">mdi-lightbulb-on</v-icon>
                      {{ sugerencias.length >= 2 ? 'Desglose de Recibos Agrupados' : 'Sugerencia de Conciliación Automática (Score: ' + sugerencias[0].score + '%)' }}
                    </div>

                    <v-card
                      v-for="sug in sugerencias"
                      :key="sug.id"
                      outlined
                      :class="sug.estado === 'CONFIRMADO' ? 'mb-3 pa-3 border-success green lighten-5' : 'mb-3 pa-3 border-orange orange lighten-5'"
                      style="border-width: 2px; border-style: solid; border-radius: 8px;"
                    >
                      <!-- Header de la tarjeta con Estado y Score -->
                      <div class="d-flex justify-space-between align-center mb-1">
                        <div class="caption font-weight-bold d-flex align-center">
                          <v-icon :color="sug.estado === 'CONFIRMADO' ? 'success' : 'orange darken-3'" class="mr-1" small>
                            {{ sug.estado === 'CONFIRMADO' ? 'mdi-check-decagram' : 'mdi-lightbulb-on' }}
                          </v-icon>
                          <span :class="sug.estado === 'CONFIRMADO' ? 'green--text text--darken-3' : 'orange--text text--darken-3'">
                            {{ sug.estado === 'CONFIRMADO' ? 'CONCILIADO AUTOMÁTICAMENTE' : 'SUGERENCIA DETALLADA' }}
                          </span>
                        </div>
                        <v-chip
                          x-small
                          :color="sug.estado === 'CONFIRMADO' ? 'success' : 'orange darken-3'"
                          dark
                          class="font-weight-bold"
                        >
                          Score: {{ sug.score }}%
                        </v-chip>
                      </div>

                      <v-divider class="my-2" />

                      <!-- Información del Estudiante y Recibo -->
                      <v-row dense align="center">
                        <v-col cols="12" sm="8">
                          <!-- Badge Destacado de N° DE RECIBO y Chip PAGO QR -->
                          <div class="mb-1 d-flex align-center flex-wrap">
                            <v-chip
                              small
                              color="primary lighten-5"
                              text-color="primary darken-3"
                              class="font-weight-bold mr-2 mb-1"
                              style="border: 1px solid #1976D2;"
                            >
                              <v-icon small color="primary" class="mr-1">mdi-receipt</v-icon>
                              RECIBO N°: {{ (sug.recibo && sug.recibo.nro_recibo) || sug.pagos_inscripcion_id }}
                            </v-chip>
                            <v-chip
                              v-if="sug.recibo && sug.recibo.forma_pago_id === 5"
                              small
                              color="purple"
                              dark
                              class="font-weight-bold mb-1"
                            >
                              <v-icon x-small class="mr-1">mdi-qrcode-scan</v-icon>
                              PAGO QR
                            </v-chip>
                          </div>

                          <!-- Nombre y Documento del Estudiante / Depositante -->
                          <div class="body-2 font-weight-bold black--text d-flex align-center">
                            <v-icon small color="primary" class="mr-1">mdi-account-school</v-icon>
                            {{ (sug.recibo && sug.recibo.estudiante_nombre) || (sug.recibo && sug.recibo.nombre_depositante) || 'Depositante Desconocido' }}
                          </div>
                          <div class="caption grey--text text--darken-2 pl-5" v-if="sug.recibo && (sug.recibo.estudiante_doc || sug.recibo.num_doc_depositante)">
                            <strong>CI / NIT:</strong> {{ sug.recibo.estudiante_doc || sug.recibo.num_doc_depositante }}
                          </div>
                          <div class="caption primary--text font-weight-medium pl-5" v-if="sug.recibo && sug.recibo.programa_nombre">
                            <v-icon x-small color="primary">mdi-school</v-icon> {{ sug.recibo.programa_nombre }}
                          </div>

                          <!-- Pills de Recibo, Comprobante, Fecha y Monto -->
                          <div class="d-flex flex-wrap align-center mt-1">
                            <v-chip x-small color="blue lighten-5" text-color="blue darken-3" class="mr-1 mb-1 font-weight-bold" v-if="sug.recibo && sug.recibo.num_comprobante">
                              Ref: {{ sug.recibo.num_comprobante }}
                            </v-chip>
                            <v-chip x-small color="purple lighten-5" text-color="purple darken-3" class="mr-1 mb-1" v-if="sug.recibo && sug.recibo.fecha_pago">
                              Fecha: {{ sug.recibo.fecha_pago }}
                            </v-chip>
                            <v-chip x-small color="green lighten-4" text-color="green darken-4" class="mb-1 font-weight-bold">
                              Bs {{ formatMonto(sug.monto_asociado) }}
                            </v-chip>
                          </div>
                        </v-col>

                        <!-- Botones de Acción -->
                        <v-col cols="12" sm="4" class="text-right d-flex flex-column align-end justify-center">
                          <template v-if="sug.estado === 'SUGERIDO'">
                            <v-btn
                              color="success"
                              small
                              block
                              class="mb-2 font-weight-bold"
                              @click="confirmarMatch(sug)"
                              :loading="procesandoMatch"
                            >
                              <v-icon left small>mdi-check</v-icon> APROBAR
                            </v-btn>
                            <v-btn
                              color="error"
                              outlined
                              x-small
                              block
                              @click="desvincularMatch(sug)"
                              :loading="procesandoDesvincular"
                            >
                              DESVINCULAR
                            </v-btn>
                          </template>
                          <template v-else>
                            <v-chip color="success" small class="mb-1 font-weight-bold">
                              <v-icon left small>mdi-check-circle</v-icon> CONCILIADO
                            </v-chip>
                            <v-btn
                              color="warning"
                              text
                              x-small
                              @click="desvincularMatch(sug)"
                              :loading="procesandoDesvincular"
                            >
                              <v-icon left x-small>mdi-link-off</v-icon> DESVINCULAR
                            </v-btn>
                          </template>
                        </v-col>

                        <!-- Tabla Comparativa Fija Full-Width -->
                        <v-col cols="12" class="pt-1">
                          <v-card outlined class="grey lighten-5" style="border-radius: 6px; overflow: hidden;">
                            <div class="px-2 py-1 grey lighten-3 d-flex justify-space-between align-center border-bottom" style="font-size: 11px; font-weight: 700;">
                              <span>
                                <v-icon x-small color="primary" class="mr-1">mdi-table-search</v-icon>
                                Desglose Matemático Comparativo
                              </span>
                              <span class="green--text text--darken-3 font-weight-bold">
                                Suma: {{ (sug.recibo && sug.recibo.score_breakdown && sug.recibo.score_breakdown.total_pts) || sug.score }} / 100 PTS
                              </span>
                            </div>
                            <v-simple-table dense style="font-size: 11px;">
                              <template v-slot:default>
                                <thead>
                                  <tr class="grey lighten-4">
                                    <th class="text-left px-2 py-0" style="height:20px; font-size:11px; width:22%;">Criterio</th>
                                    <th class="text-left px-2 py-0" style="height:20px; font-size:11px; width:33%;">Extracto Bancario</th>
                                    <th class="text-left px-2 py-0" style="height:20px; font-size:11px; width:35%;">Recibo / Sistema</th>
                                    <th class="text-right px-2 py-0" style="height:20px; font-size:11px; width:10%;">Puntos</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="(item, idx) in (sug.recibo && sug.recibo.score_breakdown && sug.recibo.score_breakdown.items) || []" :key="idx">
                                    <td class="font-weight-medium px-2 py-0" style="height:20px; font-size:11px; white-space: nowrap;">
                                      <v-icon x-small :color="item.matched ? 'success' : 'grey lighten-1'" class="mr-1" style="font-size: 11px;">
                                        {{ item.matched ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}
                                      </v-icon>
                                      {{ item.criterio }}
                                    </td>
                                    <td class="grey--text text--darken-3 px-2 py-0" style="height:20px; font-size:11px;">{{ item.val_ext }}</td>
                                    <td class="grey--text text--darken-3 px-2 py-0" style="height:20px; font-size:11px;">{{ item.val_rec }}</td>
                                    <td class="text-right font-weight-bold px-2 py-0" style="height:20px; font-size:11px;" :class="item.puntos > 0 ? 'green--text text--darken-2' : 'grey--text'">
                                      {{ item.puntos > 0 ? '+' + item.puntos + ' pts' : '0 pts' }}
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card>
                  </div>

                  <v-divider class="my-3" v-if="sugerencias && sugerencias.length > 0" />

                  <!-- Sección Conciliación Manual N-a-M -->
                  <div class="subtitle-2 font-weight-bold mb-2">
                    <v-icon small color="purple">mdi-hand-pointing-up</v-icon> Conciliación Manual (Vincular Recibos)
                  </div>
                  <v-row dense class="mb-2">
                    <v-col cols="8">
                      <v-text-field
                        v-model="busquedaRecibo"
                        label="N° de Recibo (Ej. 528900)"
                        placeholder="Ingresa el N° de recibo"
                        dense
                        outlined
                        hide-details
                        @keyup.enter="agregarReciboManual"
                      />
                    </v-col>
                    <v-col cols="4">
                      <v-btn
                        color="purple"
                        dark
                        block
                        dense
                        @click="agregarReciboManual"
                        :loading="buscandoRecibo"
                        :disabled="buscandoRecibo"
                      >
                        <v-icon left small>mdi-plus</v-icon> Agregar
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!-- Alerta de Error/Advertencia de Búsqueda -->
                  <v-alert
                    v-if="errorBusquedaRecibo"
                    type="error"
                    dense
                    dismissible
                    class="caption py-1 my-2"
                    @input="errorBusquedaRecibo = ''"
                  >
                    {{ errorBusquedaRecibo }}
                  </v-alert>

                  <!-- Recibos Seleccionados Verificados para el Match Manual -->
                  <v-simple-table dense v-if="recibosManuales.length > 0">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>N° Recibo</th>
                          <th>Estudiante / Depositante</th>
                          <th>Fecha</th>
                          <th>Monto Real (Bs)</th>
                          <th>Observaciones</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(rec, idx) in recibosManuales" :key="idx">
                          <td class="font-weight-bold text-no-wrap">
                            RECIBO N° {{ rec.nro_recibo || rec.pagos_inscripcion_id }}
                          </td>
                          <td class="caption">
                            <div class="font-weight-bold">{{ rec.estudiante_nombre }}</div>
                            <div class="grey--text">CI/NIT: {{ rec.estudiante_doc }}</div>
                          </td>
                          <td class="caption text-no-wrap">{{ rec.fecha_pago }}</td>
                          <td>
                            <v-text-field
                              v-model="rec.monto"
                              dense
                              hide-details
                              type="number"
                              style="width: 100px"
                              class="font-weight-bold green--text"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model="rec.observaciones"
                              dense
                              hide-details
                              placeholder="Opcional"
                            />
                          </td>
                          <td>
                            <v-btn icon color="red" small @click="quitarReciboManual(idx)">
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                  <div class="mt-3 d-flex justify-space-between align-center" v-if="recibosManuales.length > 0">
                    <span class="caption font-weight-bold">
                      Suma Seleccionada: Bs {{ sumaRecibosManuales.toFixed(2) }}
                    </span>
                    <v-btn
                      color="primary"
                      @click="ejecutarConciliacionManual"
                      :loading="procesandoMatch"
                    >
                      <v-icon left>mdi-content-save-check</v-icon> Guardar Conciliación Manual
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <v-card outlined class="pa-6 text-center grey--text text--lighten-1" v-else>
                <v-icon size="48" color="grey lighten-2">mdi-cursor-default-click-outline</v-icon>
                <div class="subtitle-1 mt-2">Selecciona un movimiento del extracto a la izquierda para ver opciones de conciliación</div>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- ---------------------------------------------------- -->
        <!-- TAB 2: CARGAR EXTRACTO BANCARIO -->
        <!-- ---------------------------------------------------- -->
        <v-tab-item key="tab-cargas" class="pa-4">
          <v-row justify="center">
            <v-col cols="12" md="8">
              <v-card outlined class="pa-4">
                <v-card-title class="subtitle-1 font-weight-bold">
                  <v-icon left color="primary">mdi-file-upload-outline</v-icon> Subir Archivo de Extracto Bancario
                </v-card-title>
                <v-form ref="formCarga" v-model="formCargaValido">
                  <v-row dense class="mt-2">
                    <v-col cols="12" sm="4">
                      <v-select
                        v-model="formCarga.banco"
                        :items="['BISA', 'BNB', 'UNION']"
                        label="Banco *"
                        outlined
                        dense
                        :rules="[v => !!v || 'Selecciona un banco']"
                      />
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field
                        v-model="formCarga.nro_cuenta"
                        label="N° de Cuenta Bancaria"
                        placeholder="Ej. 8128740024"
                        outlined
                        dense
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-file-input
                        v-model="formCarga.archivo"
                        label="Archivo (.csv / .xlsx) *"
                        outlined
                        dense
                        accept=".csv, .xlsx, .xls"
                        :rules="[v => !!v || 'Selecciona un archivo']"
                        show-size
                      />
                    </v-col>
                    <v-col cols="12" class="text-right d-flex justify-end gap-2">
                      <v-btn
                        color="info"
                        outlined
                        large
                        class="mr-2"
                        @click="ejecutarPrevisualizacion"
                        :loading="previsualizando"
                        :disabled="!formCargaValido"
                      >
                        <v-icon left>mdi-eye-outline</v-icon> 1. Previsualizar Datos
                      </v-btn>
                      <v-btn
                        color="success"
                        large
                        @click="subirArchivoExtracto"
                        :loading="subiendoArchivo"
                        :disabled="!formCargaValido"
                      >
                        <v-icon left>mdi-upload</v-icon> 2. Confirmar e Importar
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>

                <!-- Previsualización de Movimientos Parseados -->
                <v-card outlined v-if="datosPreview" class="mt-4 pa-3 blue lighten-5">
                  <v-card-title class="subtitle-1 font-weight-bold py-1 d-flex justify-space-between">
                    <span>
                      <v-icon color="info" class="mr-1">mdi-table-search</v-icon>
                      Previsualización de Extracto: {{ datosPreview.nombre_archivo }} ({{ datosPreview.banco }})
                    </span>
                    <div>
                      <v-chip small color="primary" dark class="mr-2">
                        Filas: {{ datosPreview.filas_totales }}
                      </v-chip>
                      <v-chip small color="success" dark>
                        Total Créditos: Bs {{ Number(datosPreview.total_creditos).toFixed(2) }}
                      </v-chip>
                    </div>
                  </v-card-title>

                  <v-data-table
                    :headers="headersPreview"
                    :items="datosPreview.movimientos"
                    dense
                    class="elevation-1 mt-2 white"
                  >
                    <template v-slot:[`item.fecha`]="{ item }">
                      {{ item.fecha ? item.fecha.substring(0, 10) : '' }}
                    </template>
                    <template v-slot:[`item.hora`]="{ item }">
                      {{ formatHora(item.hora) }}
                    </template>
                    <template v-slot:[`item.credito`]="{ item }">
                      <span class="green--text font-weight-bold">
                        Bs {{ Number(item.credito).toFixed(2) }}
                      </span>
                    </template>
                    <template v-slot:[`item.glosa`]="{ item }">
                      {{ item.nombre_depositante || item.glosa || item.descripcion || '-' }}
                    </template>
                  </v-data-table>
                </v-card>

                <!-- Resumen de última carga realizada -->
                <v-alert v-if="resultadoCarga" type="success" dismissible class="mt-4" border="left">
                  <div class="subtitle-2 font-weight-bold">Carga procesada correctamente:</div>
                  <div>- Filas Totales: <strong>{{ resultadoCarga.filas_totales }}</strong></div>
                  <div>- Filas Nuevas Insertadas: <strong>{{ resultadoCarga.filas_insertadas }}</strong></div>
                  <div>- Filas Duplicadas Omite: <strong>{{ resultadoCarga.filas_duplicadas }}</strong></div>
                  <div>- Conciliaciones Automáticas Sugeridas: <strong>{{ resultadoCarga.filas_conciliadas_auto }}</strong></div>
                </v-alert>
              </v-card>

              <!-- Tabla de historial de archivos subidos -->
              <v-card outlined class="mt-6">
                <v-card-title class="subtitle-1 font-weight-bold">
                  Historial de Archivos Cargados
                </v-card-title>
                <v-data-table
                  :headers="headersCargas"
                  :items="cargasHistorial"
                  :loading="cargandoCargas"
                  dense
                >
                  <template v-slot:[`item.fecha_carga`]="{ item }">
                    {{ formatearFechaHora(item.fecha_carga) }}
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- ---------------------------------------------------- -->
        <!-- TAB 3: LISTADO DE EXTRACTOS -->
        <!-- ---------------------------------------------------- -->
        <v-tab-item key="tab-extractos" class="pa-4">
          <v-data-table
            :headers="headersExtractosCompleto"
            :items="extractos"
            :loading="cargandoExtractos"
            dense
            class="elevation-1"
          >
            <template v-slot:[`item.credito`]="{ item }">
              <span class="green--text font-weight-bold">Bs {{ Number(item.credito).toFixed(2) }}</span>
            </template>
            <template v-slot:[`item.estado`]="{ item }">
              <v-chip small :color="getColorEstado(item.estado)" dark>
                {{ item.estado || 'SIN_CONCILIAR' }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Dialogo para rechazar sugerencia -->
    <v-dialog v-model="modalRechazo" max-width="500">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold red lighten-5 red--text">
          Rechazar Sugerencia de Conciliación
        </v-card-title>
        <v-card-text class="pt-4">
          <v-textarea
            v-model="observacionRechazo"
            label="Observaciones / Motivo del rechazo *"
            outlined
            dense
            rows="3"
            placeholder="Ej. El titular del recibo no corresponde con el movimiento del banco"
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="modalRechazo = false">Cancelar</v-btn>
          <v-btn color="error" @click="confirmarRechazo" :disabled="!observacionRechazo">Rechazar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import conciliacionService from './service/conciliacion.service';

export default {
  name: 'ConciliacionBancaria',
  data() {
    return {
      tab: 0,
      cargandoExtractos: false,
      cargandoCargas: false,
      subiendoArchivo: false,
      procesandoMatch: false,

      opcionesBancos: ['BISA', 'BNB', 'UNION'],
      opcionesEstados: ['TODOS', 'SIN_CONCILIAR', 'SUGERIDO', 'CONFIRMADO'],

      filtros: {
        banco: null,
        nro_cuenta: null,
        estado: 'SIN_CONCILIAR',
        fechaDesde: null,
        fechaHasta: null,
      },

      resumenBalance: {
        periodo_conciliacion: '',
        banco: '',
        metricas_cabecera: {
          total_ingreso_banco: 0,
          total_importe_recibos_mes: 0,
          saldo_fondos_custodia: 0,
          recibos_meses_anteriores_registrados: 0,
        },
        auditoria_balance: {
          diferencia: 0,
          estado_cuadre: 'CORRECTO',
          mensaje: '',
        },
      },

      extractos: [],
      extractoSeleccionado: null,
      sugerencias: [],

      // Para match manual
      busquedaRecibo: '',
      buscandoRecibo: false,
      errorBusquedaRecibo: '',
      recibosManuales: [],

      // Carga
      formCargaValido: false,
      formCarga: {
        banco: null,
        nro_cuenta: null,
        archivo: null,
      },
      previsualizando: false,
      datosPreview: null,
      resultadoCarga: null,
      cargasHistorial: [],

      // Modal rechazo
      modalRechazo: false,
      sugerenciaARechazar: null,
      observacionRechazo: '',

      // Notificaciones Toast Flotantes (Top-Right)
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        icon: 'mdi-check-circle',
        timeout: 4000,
      },

      headersPreview: [
        { text: 'Fecha', value: 'fecha' },
        { text: 'Hora', value: 'hora' },
        { text: 'Descripción', value: 'descripcion' },
        { text: 'Depositante / Glosa', value: 'glosa' },
        { text: 'Referencia', value: 'referencia' },
        { text: 'Crédito (Bs)', value: 'credito' },
      ],

      headersExtractos: [
        { text: 'ID', value: 'id' },
        { text: 'Banco', value: 'banco' },
        { text: 'Fecha', value: 'fecha_movimiento' },
        { text: 'Crédito', value: 'credito' },
        { text: 'Estado', value: 'estado' },
      ],

      headersExtractosCompleto: [
        { text: 'ID', value: 'id' },
        { text: 'Banco', value: 'banco' },
        { text: 'Fecha', value: 'fecha_movimiento' },
        { text: 'Glosa / Desc', value: 'glosa' },
        { text: 'Referencia', value: 'referencia' },
        { text: 'Crédito', value: 'credito' },
        { text: 'Estado', value: 'estado' },
      ],

      headersCargas: [
        { text: 'ID', value: 'id' },
        { text: 'Banco', value: 'banco' },
        { text: 'Nombre Archivo', value: 'nombre_archivo' },
        { text: 'Filas Totales', value: 'filas_totales' },
        { text: 'Conciliadas Auto', value: 'filas_conciliadas_auto' },
        { text: 'Fecha Carga', value: 'fecha_carga' },
      ],
    };
  },
  computed: {
    userId() {
      return this.$store.state.DATA_USER?.id || 1;
    },
    sumaRecibosManuales() {
      return this.recibosManuales.reduce((acc, curr) => acc + (Number(curr.monto) || 0), 0);
    },
    esCuadrado() {
      return Number(this.resumenBalance?.auditoria_balance?.diferencia || 0) === 0;
    },
  },
  mounted() {
    this.cargarExtractosTablero();
    this.cargarHistorialCargas();
  },
  methods: {
    notificar(texto, tipo = 'success', icono = null) {
      const iconos = {
        success: 'mdi-check-circle',
        warning: 'mdi-alert',
        error: 'mdi-alert-circle',
        info: 'mdi-information',
      };
      this.snackbar.text = texto;
      this.snackbar.color = tipo;
      this.snackbar.icon = icono || iconos[tipo] || 'mdi-bell';
      this.snackbar.show = true;
    },
    formatHora(h) {
      if (!h) return '-';
      if (typeof h === 'string' && h.includes('T')) {
        const parts = h.split('T');
        if (parts.length > 1) return parts[1].substring(0, 5);
      }
      return String(h).substring(0, 5);
    },
    refrescarTodo() {
      this.cargarExtractosTablero();
      this.cargarHistorialCargas();
      this.notificar('Tablero y métricas de balance actualizadas', 'info');
    },
    async cargarResumenBalance() {
      try {
        const params = {};
        if (this.filtros.banco) params.banco = this.filtros.banco;
        if (this.filtros.nro_cuenta) params.nro_cuenta = this.filtros.nro_cuenta;
        if (this.filtros.fechaDesde) params.fecha_desde = this.filtros.fechaDesde;
        if (this.filtros.fechaHasta) params.fecha_hasta = this.filtros.fechaHasta;

        const resp = await conciliacionService.obtenerResumenBalance(params, this.userId);
        if (resp.data) {
          this.resumenBalance = resp.data;
        }
      } catch (err) {
        window.console.error('Error al cargar resumen de balance:', err);
      }
    },
    async cargarExtractosTablero() {
      this.cargandoExtractos = true;
      this.cargarResumenBalance();
      try {
        const params = {};
        if (this.filtros.banco) params.banco = this.filtros.banco;
        if (this.filtros.nro_cuenta) params.nro_cuenta = this.filtros.nro_cuenta;
        if (this.filtros.estado && this.filtros.estado !== 'TODOS') {
          params.estado_conciliacion = this.filtros.estado;
        }
        if (this.filtros.fechaDesde) params.fecha_desde = this.filtros.fechaDesde;
        if (this.filtros.fechaHasta) params.fecha_hasta = this.filtros.fechaHasta;

        const resp = await conciliacionService.listarExtractos(params, this.userId);
        this.extractos = Array.isArray(resp.data?.data) ? resp.data.data : (Array.isArray(resp.data) ? resp.data : []);
        this.extractoSeleccionado = null;
        this.sugerencias = [];
        this.recibosManuales = [];
      } catch (err) {
        window.console.error('Error al listar extractos:', err);
        this.notificar('Error al cargar movimientos bancarios', 'error');
      } finally {
        this.cargandoExtractos = false;
      }
    },
    async seleccionarExtracto(item) {
      this.extractoSeleccionado = item;
      this.recibosManuales = [];
      try {
        const resp = await conciliacionService.obtenerExtractoDetalle(item.id, this.userId);
        this.sugerencias = resp.data?.conciliaciones || [];
      } catch (err) {
        window.console.error('Error al obtener detalle extracto:', err);
        this.sugerencias = [];
      }
    },
    async confirmarMatch(sug) {
      this.procesandoMatch = true;
      try {
        await conciliacionService.confirmarConciliacion(
          this.extractoSeleccionado.id,
          sug.id,
          this.userId
        );
        const nroRec = (sug.recibo && sug.recibo.nro_recibo) || sug.pagos_inscripcion_id;
        this.notificar(`✅ Conciliación de Recibo N° ${nroRec} aprobada correctamente`, 'success');
        this.cargarExtractosTablero();
      } catch (err) {
        window.console.error('Error al confirmar match:', err);
        this.notificar('❌ Error al confirmar la conciliación', 'error');
      } finally {
        this.procesandoMatch = false;
      }
    },
    abrirModalRechazo(sug) {
      this.sugerenciaARechazar = sug;
      this.observacionRechazo = '';
      this.modalRechazo = true;
    },
    async confirmarRechazo() {
      if (!this.sugerenciaARechazar) return;
      this.procesandoMatch = true;
      try {
        await conciliacionService.rechazarConciliacion(
          this.extractoSeleccionado.id,
          this.sugerenciaARechazar.id,
          this.observacionRechazo,
          this.userId
        );
        this.modalRechazo = false;
        this.notificar('⚠️ Sugerencia de conciliación rechazada', 'warning');
        this.cargarExtractosTablero();
      } catch (err) {
        window.console.error('Error al rechazar match:', err);
        this.notificar('❌ Error al rechazar la conciliación', 'error');
      } finally {
        this.procesandoMatch = false;
      }
    },
    async agregarReciboManual() {
      this.errorBusquedaRecibo = '';
      const nro = Number(this.busquedaRecibo);
      if (!this.busquedaRecibo || !nro || isNaN(nro)) {
        this.errorBusquedaRecibo = 'Por favor ingresa un N° de Recibo válido (ej. 528900).';
        this.notificar(this.errorBusquedaRecibo, 'warning');
        return;
      }

      if (this.recibosManuales.some(r => r.nro_recibo === nro)) {
        this.errorBusquedaRecibo = `El Recibo N° ${nro} ya fue agregado a la lista actual.`;
        this.notificar(this.errorBusquedaRecibo, 'warning');
        return;
      }

      this.buscandoRecibo = true;
      try {
        const resp = await conciliacionService.buscarReciboPorNro(nro, this.userId);
        const data = resp.data;

        if (data.ya_conciliado) {
          this.errorBusquedaRecibo = `⚠️ El Recibo N° ${nro} ya fue conciliado previamente (estado CONFIRMADO).`;
          this.notificar(this.errorBusquedaRecibo, 'warning');
          return;
        }

        this.recibosManuales.push({
          pagos_inscripcion_id: data.id,
          nro_recibo: data.nro_recibo,
          estudiante_nombre: data.estudiante_nombre || data.nombre_depositante || 'Depositante Desconocido',
          estudiante_doc: data.estudiante_doc || data.num_doc_depositante || '-',
          fecha_pago: data.fecha_pago ? data.fecha_pago.substring(0, 10) : '-',
          monto: data.monto_real || 0,
          observaciones: 'Conciliación manual verificada',
        });

        this.notificar(`✅ Recibo N° ${data.nro_recibo} verificado y agregado a la lista`, 'success');
        this.busquedaRecibo = '';
      } catch (err) {
        if (err.response && err.response.status === 404) {
          this.errorBusquedaRecibo = `❌ No se encontró ningún recibo activo con el N° ${nro}.`;
          this.notificar(this.errorBusquedaRecibo, 'error');
        } else if (err.response && err.response.status === 409 && err.response.data?.mensaje) {
          this.errorBusquedaRecibo = `⚠️ ${err.response.data.mensaje}`;
          this.notificar(this.errorBusquedaRecibo, 'warning');
        } else {
          this.errorBusquedaRecibo = `Ocurrió un error al buscar el recibo N° ${nro}.`;
          this.notificar(this.errorBusquedaRecibo, 'error');
        }
      } finally {
        this.buscandoRecibo = false;
      }
    },
    quitarReciboManual(index) {
      this.recibosManuales.splice(index, 1);
      this.notificar('Item removido de la lista manual', 'info');
    },
    async ejecutarConciliacionManual() {
      if (!this.extractoSeleccionado || this.recibosManuales.length === 0) return;
      this.procesandoMatch = true;
      try {
        await conciliacionService.conciliarManual(
          this.extractoSeleccionado.id,
          this.recibosManuales,
          this.userId
        );
        this.notificar(`✅ Conciliación manual de ${this.recibosManuales.length} recibo(s) guardada con éxito`, 'success');
        this.cargarExtractosTablero();
      } catch (err) {
        window.console.error('Error en conciliación manual:', err);
        this.notificar('❌ Error al guardar conciliación manual', 'error');
      } finally {
        this.procesandoMatch = false;
      }
    },
    async ejecutarPrevisualizacion() {
      if (!this.formCarga.banco || !this.formCarga.archivo) return;
      this.previsualizando = true;
      this.datosPreview = null;
      try {
        const formData = new FormData();
        formData.append('banco', this.formCarga.banco);
        formData.append('archivo', this.formCarga.archivo);
        if (this.formCarga.nro_cuenta) {
          formData.append('nro_cuenta', this.formCarga.nro_cuenta);
        }

        const resp = await conciliacionService.previsualizarExtracto(formData, this.userId);
        this.datosPreview = resp.data;
        this.notificar(`ℹ️ Previsualización generada: ${resp.data.filas_totales} filas en extracto ${resp.data.banco}`, 'info');
      } catch (err) {
        window.console.error('Error al previsualizar extracto:', err);
        this.notificar('❌ Error al previsualizar el archivo de extracto', 'error');
      } finally {
        this.previsualizando = false;
      }
    },
    async subirArchivoExtracto() {
      if (!this.formCarga.banco || !this.formCarga.archivo) return;
      this.subiendoArchivo = true;
      try {
        const formData = new FormData();
        formData.append('banco', this.formCarga.banco);
        formData.append('archivo', this.formCarga.archivo);
        if (this.formCarga.nro_cuenta) {
          formData.append('nro_cuenta', this.formCarga.nro_cuenta);
        }

        const resp = await conciliacionService.subirExtracto(formData, this.userId);
        this.resultadoCarga = resp.data;
        this.formCarga.archivo = null;
        this.notificar(`✅ Extracto importado: ${resp.data.filas_insertadas} filas insertadas, ${resp.data.filas_conciliadas_auto} auto-conciliadas`, 'success');
        this.cargarHistorialCargas();
        this.cargarExtractosTablero();
      } catch (err) {
        window.console.error('Error al subir extracto:', err);
        this.notificar('❌ Error al importar archivo de extracto', 'error');
      } finally {
        this.subiendoArchivo = false;
      }
    },
    async cargarHistorialCargas() {
      this.cargandoCargas = true;
      try {
        const resp = await conciliacionService.listarCargas({}, this.userId);
        this.cargasHistorial = Array.isArray(resp.data?.data) ? resp.data.data : (Array.isArray(resp.data) ? resp.data : []);
      } catch (err) {
        window.console.error('Error al cargar historial cargas:', err);
      } finally {
        this.cargandoCargas = false;
      }
    },
    async aprobarGrupoRecibos(sugerencias) {
      if (!sugerencias || sugerencias.length === 0) return;
      this.guardandoMatch = true;
      try {
        for (const sug of sugerencias) {
          await conciliacionService.confirmarConciliacion(this.extractoSeleccionado.id, sug.id, this.userId);
        }
        this.notificar(`✅ Grupo de ${sugerencias.length} recibos verificado y confirmado con éxito`, 'success');
        this.cargarExtractosTablero();
        if (this.extractoSeleccionado) {
          this.seleccionarExtracto(this.extractoSeleccionado);
        }
      } catch (err) {
        window.console.error('Error al aprobar grupo:', err);
        this.notificar('❌ Error al aprobar el grupo multi-recibo', 'error');
      } finally {
        this.guardandoMatch = false;
      }
    },
    calcularTotalSugerencias(sugerencias) {
      if (!sugerencias) return 0;
      return sugerencias.reduce((acc, curr) => acc + (Number(curr.monto_asociado) || 0), 0);
    },
    formatMonto(val) {
      if (val === null || val === undefined || val === '') return '0,00';
      const num = Number(val);
      if (isNaN(num)) return val;
      return num.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    getColorEstado(estado) {
      switch (estado) {
        case 'CONFIRMADO': return 'success';
        case 'SUGERIDO': return 'warning';
        case 'RECHAZADO': return 'error';
        default: return 'grey';
      }
    },
    getFilaClass(item) {
      return this.extractoSeleccionado && this.extractoSeleccionado.id === item.id
        ? 'selected-extracto-row font-weight-bold'
        : '';
    },
    formatearFechaHora(fechaStr) {
      if (!fechaStr) return '';
      return new Date(fechaStr).toLocaleString();
    },
  },
};
</script>

<style scoped>
.border-orange {
  border: 1px solid #fb8c00 !important;
}
.selected-extracto-row {
  background-color: #E3F2FD !important;
  border-left: 4px solid #1976D2 !important;
}
.dashboard-panel-card {
  height: calc(100vh - 210px);
  min-height: 500px;
  display: flex;
  flex-direction: column;
}
.scrollable-detail-body {
  height: calc(100vh - 270px);
  overflow-y: auto;
}
</style>
